/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Row, Col, Form, message, Modal, TabsProps, Tabs, Alert } from 'antd';
import { Button, Input } from '@cloudcar-app/cloudcar-components';
import { useHistory, useLocation } from 'react-router-dom';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
  concessionaireLogoUrl,
  concessionaireName,
  logged,
} from 'apollo/cache';
import apiService from 'utils/apiService';
import MessageError from 'utils/message.errors';
import rutFormValidator from 'utils/rutFormValidator';
import { validateRut, formatRut } from '@abstract-cl/rut-ts';
import { User } from 'apollo/types';
import operations from 'apollo/operations';
import { InfoCircleOutlined } from '@ant-design/icons';
import config from 'config';
import { trimFormValues } from 'utils/trimFormValues';
import styles from './Login.module.scss';

interface LoginValues {
  username: string;
  password: string;
}

interface CognitoTokens {
  IdToken: string;
  RefreshToken: string;
  AccessToken: string;
}

const Login: React.FC = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Check if there is an 'error' parameter in the query string
  const error = queryParams.get('error');

  const { stage } = config;
  const history = useHistory();
  const [form] = Form.useForm();
  const isUserLogged = useReactiveVar(logged);
  const [forgotPasswordForm] = Form.useForm();
  const [requested, setRequested] = useState(false);
  const [forgotPasswordRequested, setForgotPasswordRequested] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const logoUrl = useReactiveVar(concessionaireLogoUrl);
  const name = useReactiveVar(concessionaireName);

  const [getLoggedUser, { data: userData }] = useLazyQuery<{ getUser: User }>(
    operations.GET_USER,
    {
      variables: {
        cognitoId: localStorage.getItem('AccessToken') || '',
      },
    },
  );

  const handleChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      form.setFieldsValue({ username: formatted });
      return rut;
    }
    form.setFieldsValue({
      username: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  const handleForgotPasswordChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      forgotPasswordForm.setFieldsValue({ forgotPasswordUsername: formatted });
      return rut;
    }
    forgotPasswordForm.setFieldsValue({
      forgotPasswordUsername: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  const forgotPasswordSubmit = async (values: {
    forgotPasswordUsername: string;
  }) => {
    try {
      setForgotPasswordRequested(true);
      const { forgotPasswordUsername } = trimFormValues(values);
      await apiService.forgotPasswordRequest(forgotPasswordUsername);
      message.loading(
        'Te hemos enviado un correo con las instrucciones para restablecer tu contraseña',
        3,
      );
      setShowForgotPasswordModal(false);
      setForgotPasswordRequested(false);
    } catch (err) {
      setForgotPasswordRequested(false);
      message.loading('Error', 1);
    }
  };

  const renderPageTitle = () => {
    return 'Simulación de Trámites Adicionales';
  };

  const idpSso = () => {
    window.location.href =
      'https://lite-api-prod.auth.us-east-1.amazoncognito.com/authorize?response_type=code&identity_provider=kaufmann&client_id=e9vv86rovj4i49h59gfklritc&redirect_uri=https://lite-cloudcar-api.cloudcar.cl/idp-fallback';
  };

  useEffect(() => {
    if (error && error === 'invalid_login') {
      message.error(
        'Error al iniciar sesión. Verifique con el administrador que su cuenta se encuentre activa.',
        5,
      );
    }
  }, [error]);

  const sendForm = async (values: LoginValues) => {
    try {
      const { username, password } = trimFormValues(values);
      const { host } = window.location;
      const concessionaire = host.split('.')[0];
      setRequested(true);
      message.loading('Iniciando sesión...', 3);
      const data = {
        username,
        password,
        concessionaireName:
          concessionaire !== 'localhost' ? 'Kaufmann' : 'Kaufmann',
      };
      await apiService.loginRequest(data).then((response) => {
        const tokens = response.response as CognitoTokens;
        localStorage.setItem('RefreshToken', tokens.RefreshToken);
        localStorage.setItem('AccessToken', tokens.AccessToken);
        localStorage.setItem('IdToken', tokens.IdToken);
        logged(true);
        setRequested(false);
      });
    } catch (err) {
      setRequested(false);
      message.loading('Error', 1);
    }
  };

  useEffect(() => {
    localStorage.clear();
  }, []);

  useEffect(() => {
    if (isUserLogged) {
      getLoggedUser();
    }
  }, [isUserLogged]);

  useEffect(() => {
    if (userData?.getUser) {
      history.push('/');
    }
  }, [userData]);

  useEffect(() => {
    const { hash } = window.location;
    const idTokenMatch = hash.match(/id_token=([^&]+)/);
    const accessTokenMatch = hash.match(/access_token=([^&]+)/);
    const refreshTokenMatch = hash.match(/refresh_token=([^&]+)/);

    if (idTokenMatch || accessTokenMatch || refreshTokenMatch) {
      message.loading('Iniciando sesión...', 3);
      const tokens = {
        IdToken: idTokenMatch ? idTokenMatch[1] : '',
        AccessToken: accessTokenMatch ? accessTokenMatch[1] : '',
        RefreshToken: refreshTokenMatch ? refreshTokenMatch[1] : '',
      };
      localStorage.setItem('RefreshToken', tokens.RefreshToken);
      localStorage.setItem('AccessToken', tokens.AccessToken);
      localStorage.setItem('IdToken', tokens.IdToken);
      logged(true);
    }
  }, []);

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Usuario Kaufmann',
      children: (
        <Col>
          <Row
            className={styles.ButtonContainer}
            justify="center"
            gutter={[0, 12]}
          >
            <Col xs={24}>
              <Row justify="center" align="middle" gutter={[12, 12]}>
                <p>
                  Accede utilizando tu dirección de correo electrónico y
                  contraseña corporativa.
                </p>
                <Alert
                  showIcon
                  icon={<InfoCircleOutlined className={styles.AlertIcon} />}
                  message={
                    <>
                      Ten en cuenta que la cuenta debe ser activada por el
                      administrador antes de poder iniciar sesión.
                    </>
                  }
                  type="info"
                />
              </Row>
            </Col>
            <Col xs={24}>
              <Row justify="center" align="middle">
                <Button
                  size="large"
                  loading={requested}
                  disabled={requested}
                  onClick={stage === 'dev' ? () => {} : idpSso}
                >
                  Iniciar sesión
                </Button>
              </Row>
            </Col>
          </Row>
        </Col>
      ),
    },
    {
      key: '2',
      label: 'Usuario',
      children: (
        <Col>
          <Form form={form} name="login" onFinish={sendForm}>
            <Col md={24}>
              <Form.Item
                name="username"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: MessageError.emptyFieldsMessages.rut,
                  },
                  {
                    validator: rutFormValidator,
                  },
                  {
                    whitespace: true,
                    message: MessageError.emptyFieldsMessages.rut,
                  },
                ]}
              >
                <Input
                  label="Rut"
                  placeholder="11.111.111-1"
                  extra="*Escribe tu rut sin puntos ni guión."
                  onChange={(e) => handleChangeRut(e.target.value)}
                  required
                />
              </Form.Item>
            </Col>
            <Col md={24} className={styles.MarginTop}>
              <Form.Item
                name="password"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: MessageError.emptyFieldsMessages.password,
                  },
                  {
                    whitespace: true,
                    message: MessageError.emptyFieldsMessages.password,
                  },
                ]}
              >
                <Input password label="Contraseña" placeholder="contraseña" />
              </Form.Item>
            </Col>
            <Row className={styles.ButtonContainer} justify="center">
              <Button
                htmlType="submit"
                size="large"
                loading={requested}
                disabled={requested}
              >
                Iniciar sesión
              </Button>
            </Row>
            <Row className={styles.ForgotPasswordContainer} justify="center">
              <a
                href="#"
                onClick={() => {
                  setShowForgotPasswordModal(true);
                }}
              >
                Restablece tu contraseña
              </a>
            </Row>
          </Form>
        </Col>
      ),
    },
  ];

  return (
    <>
      <Modal
        visible={showForgotPasswordModal}
        onOk={() => {
          setShowForgotPasswordModal(false);
        }}
        onCancel={() => {
          setShowForgotPasswordModal(false);
        }}
        footer={null}
        width="30%"
      >
        <Form
          form={forgotPasswordForm}
          name="login"
          onFinish={forgotPasswordSubmit}
        >
          <Row justify="center" align="middle">
            <Col md={24}>
              <Form.Item
                name="forgotPasswordUsername"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: MessageError.emptyFieldsMessages.rut,
                  },
                  {
                    validator: rutFormValidator,
                  },
                  {
                    whitespace: true,
                    message: MessageError.emptyFieldsMessages.rut,
                  },
                ]}
              >
                <Input
                  label="Rut"
                  placeholder="11.111.111-1"
                  extra="*Escribe tu rut sin puntos ni guión."
                  onChange={(e) =>
                    handleForgotPasswordChangeRut(e.target.value)
                  }
                  required
                />
              </Form.Item>
            </Col>
            <Row justify="center">
              <Button
                className={styles.ForgotPasswordButtonContainer}
                htmlType="submit"
                size="large"
                loading={forgotPasswordRequested}
                disabled={forgotPasswordRequested}
              >
                Restablece tu contraseña
              </Button>
            </Row>
          </Row>
        </Form>
      </Modal>
      <Row className={styles.Container}>
        <div className={styles.Background} />
        <Col xs={0} sm={8} className={styles.FullHeight}>
          <Row justify="center" align="middle" className={styles.FullSize}>
            <Col xs={16}>
              {logoUrl && (
                <Row
                  justify="center"
                  align="middle"
                  className={styles.MarginBottom}
                >
                  <img src={logoUrl} alt={name} width="100" />
                </Row>
              )}
              <Row justify="center" align="middle">
                <h1 className={styles.CenteredText}>{renderPageTitle()}</h1>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={16} className={styles.FullHeight}>
          <Row justify="center" align="middle" className={styles.FullHeight}>
            <Col>
              <Row justify="center" align="middle" className={styles.Login}>
                <Tabs
                  style={{ width: 'auto' }}
                  defaultActiveKey="1"
                  items={items}
                  onChange={() => {}}
                />
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Login;
